import * as THREE from 'three';

export class HDRLoader extends THREE.DataTextureLoader {
  constructor() {
    super(new THREE.LoadingManager());
  }
  _parser = function(buffer: any): any {
    /* return codes for rgbe routines */
    this.RGBE_RETURN_SUCCESS = 0;
    this.RGBE_RETURN_FAILURE = -1;
    /* default error routine.  change this to change error handling */
    this.rgbe_read_error = 1;
    this.rgbe_write_error = 2;
    this.rgbe_format_error = 3;
    this.rgbe_memory_error = 4;

    /* offsets to red, green, and blue components in a data (float) pixel */
    this.RGBE_DATA_RED = 0;
    this.RGBE_DATA_GREEN = 1;
    this.RGBE_DATA_BLUE = 2;
    /* number of floats per pixel, use 4 since stored in rgba image format */
    this.RGBE_DATA_SIZE = 4;
    /* flags indicating which fields in an rgbe_header_info are valid */
    this.RGBE_VALID_PROGRAMTYPE = 1;
    this.RGBE_VALID_FORMAT = 2;
    this.RGBE_VALID_DIMENSIONS = 4;
    this.NEWLINE = '\n';

    this.rgbe_error = function(rgbe_error_code: number, msg: string) {
      switch (rgbe_error_code) {
        case this.rgbe_read_error:
          console.error('THREE.RGBELoader Read Error: ' + (msg || ''));
          break;
        case this.rgbe_write_error:
          console.error('THREE.RGBELoader Write Error: ' + (msg || ''));
          break;
        case this.rgbe_format_error:
          console.error('THREE.RGBELoader Bad File Format: ' + (msg || ''));
          break;
        default:
        case this.rgbe_memory_error:
          console.error('THREE.RGBELoader: Error: ' + (msg || ''));
      }
      return this.RGBE_RETURN_FAILURE;
    };

    // tslint:disable-next-line:no-shadowed-variable
    this.fgets = function(buffer: any, lineLimit: number, consume: any): any {
      lineLimit = !lineLimit ? 1024 : lineLimit;
      let p = buffer.pos;
      let i = -1;
      let len = 0;
      let s = '';
      const chunkSize = 128;
      let chunk = String.fromCharCode.apply(null, new Uint16Array(buffer.subarray(p, p + chunkSize)));
      while (0 > (i = chunk.indexOf(this.NEWLINE)) && len < lineLimit && p < buffer.byteLength) {
        s += chunk;
        len += chunk.length;
        p += chunkSize;
        chunk += String.fromCharCode.apply(null, new Uint16Array(buffer.subarray(p, p + chunkSize)));
      }

      if (-1 < i) {
        if (false !== consume) {
          buffer.pos += len + i + 1;
        }
        return s + chunk.slice(0, i);
      }
      return false;
    };

    /* minimal header reading.  modify if you want to parse more information */
    // tslint:disable-next-line:no-shadowed-variable
    this.RGBE_ReadHeader = function(buffer): any {
      let line;
      let match;
      // regexes to parse header info fields
      const magic_token_re = /^#\?(\S+)$/;
      const gamma_re = /^\s*GAMMA\s*=\s*(\d+(\.\d+)?)\s*$/;
      const exposure_re = /^\s*EXPOSURE\s*=\s*(\d+(\.\d+)?)\s*$/;
      const format_re = /^\s*FORMAT=(\S+)\s*$/;
      const dimensions_re = /^\s*\-Y\s+(\d+)\s+\+X\s+(\d+)\s*$/;
      // RGBE format header struct
      const header = {
        valid: 0 /* indicate which fields are valid */,
        string: '' /* the actual header string */,
        comments: '' /* comments found in header */,
        programtype: 'RGBE' /* listed at beginning of file to identify it after "#?". defaults to "RGBE" */,
        format: '' /* RGBE format, default 32-bit_rle_rgbe */,
        gamma: 1.0 /* image has already been gamma corrected with given gamma. defaults to 1.0 (no correction) */,
        exposure: 1.0 /* a value of 1.0 in an image corresponds to <exposure> watts/steradian/m^2. defaults to 1.0 */,
        width: 0,
        height: 0 /* image dimensions, width/height */
      };

      if (buffer.pos >= buffer.byteLength || !(line = this.fgets(buffer, null, null))) {
        return this.rgbe_error(this.rgbe_read_error, 'no header found');
      }
      /* if you want to require the magic token then uncomment the next line */
      if (!(match = line.match(magic_token_re))) {
        return this.rgbe_error(this.rgbe_format_error, 'bad initial token');
      }
      // tslint:disable-next-line:no-bitwise
      header.valid |= this.RGBE_VALID_PROGRAMTYPE;
      header.programtype = match[1];
      header.string += line + '\n';

      while (true) {
        line = this.fgets(buffer, null, null);
        if (false === line) { break; }
        header.string += line + '\n';

        if ('#' === line.charAt(0)) {
          header.comments += line + '\n';
          continue; // comment line
        }

        if ((match = line.match(gamma_re))) {
          header.gamma = parseFloat(match[1]); // , 10);
        }
        if ((match = line.match(exposure_re))) {
          header.exposure = parseFloat(match[1]); // , 10);
        }
        if ((match = line.match(format_re))) {
          // tslint:disable-next-line:no-bitwise
          header.valid |= this.RGBE_VALID_FORMAT;
          header.format = match[1]; // '32-bit_rle_rgbe';
        }
        if ((match = line.match(dimensions_re))) {
          // tslint:disable-next-line:no-bitwise
          header.valid |= this.RGBE_VALID_DIMENSIONS;
          header.height = parseInt(match[1], 10);
          header.width = parseInt(match[2], 10);
        }

        // tslint:disable-next-line:no-bitwise
        if (header.valid & this.RGBE_VALID_FORMAT && header.valid & this.RGBE_VALID_DIMENSIONS) {
          break;
        }
      }

      // tslint:disable-next-line:no-bitwise
      if (!(header.valid & this.RGBE_VALID_FORMAT)) {
        return this.rgbe_error(this.rgbe_format_error, 'missing format specifier');
      }
      // tslint:disable-next-line:no-bitwise
      if (!(header.valid & this.RGBE_VALID_DIMENSIONS)) {
        return this.rgbe_error(this.rgbe_format_error, 'missing image size specifier');
      }

      return header;
    };

    // tslint:disable-next-line:no-shadowed-variable
    this.RGBE_ReadPixels_RLE = function(buffer: any, w, h) {
      let data_rgba;
      let offset;
      let pos;
      let count;
      let byteValue;
      let scanline_buffer;
      let ptr;
      let ptr_end;
      let i;
      let l;
      let off;
      let isEncodedRun;
      const scanline_width = w;
      let num_scanlines = h;
      let rgbeStart;

      if (
        // run length encoding is not allowed so read flat
        scanline_width < 8 ||
        scanline_width > 0x7fff ||
        // this file is not run length encoded
        // tslint:disable-next-line:no-bitwise
        (2 !== buffer[0] || 2 !== buffer[1] || buffer[2] & 0x80)
      ) {
        // return the flat buffer
        return new Uint8Array(buffer);
      }

      // tslint:disable-next-line:no-bitwise
      if (scanline_width !== ((buffer[2] << 8) | buffer[3])) {
        return this.rgbe_error(this.rgbe_format_error, 'wrong scanline width');
      }

      data_rgba = new Uint8Array(4 * w * h);

      if (!data_rgba || !data_rgba.length) {
        return this.rgbe_error(this.rgbe_memory_error, 'unable to allocate buffer space');
      }

      offset = 0;
      pos = 0;
      ptr_end = 4 * scanline_width;
      rgbeStart = new Uint8Array(4);
      scanline_buffer = new Uint8Array(ptr_end);

      // read in each successive scanline
      while (num_scanlines > 0 && pos < buffer.byteLength) {
        if (pos + 4 > buffer.byteLength) {
          return this.rgbe_error(this.rgbe_read_error, null);
        }

        rgbeStart[0] = buffer[pos++];
        rgbeStart[1] = buffer[pos++];
        rgbeStart[2] = buffer[pos++];
        rgbeStart[3] = buffer[pos++];

        // tslint:disable-next-line:no-bitwise
        if (2 !== rgbeStart[0] || 2 !== rgbeStart[1] || ((rgbeStart[2] << 8) | rgbeStart[3]) !== scanline_width) {
          return this.rgbe_error(this.rgbe_format_error, 'bad rgbe scanline format');
        }

        // read each of the four channels for the scanline into the buffer
        // first red, then green, then blue, then exponent
        ptr = 0;
        while (ptr < ptr_end && pos < buffer.byteLength) {
          count = buffer[pos++];
          isEncodedRun = count > 128;
          if (isEncodedRun) {
            count -= 128;
          }

          if (0 === count || ptr + count > ptr_end) {
            return this.rgbe_error(this.rgbe_format_error, 'bad scanline data');
          }

          if (isEncodedRun) {
            // a (encoded) run of the same value
            byteValue = buffer[pos++];
            for (i = 0; i < count; i++) {
              scanline_buffer[ptr++] = byteValue;
            }
            // ptr += count;
          } else {
            // a literal-run
            scanline_buffer.set(buffer.subarray(pos, pos + count), ptr);
            ptr += count;
            pos += count;
          }
        }

        // now convert data from buffer into rgba
        // first red, then green, then blue, then exponent (alpha)
        l = scanline_width; // scanline_buffer.byteLength;
        for (i = 0; i < l; i++) {
          off = 0;
          data_rgba[offset] = scanline_buffer[i + off];
          off += scanline_width; // 1;
          data_rgba[offset + 1] = scanline_buffer[i + off];
          off += scanline_width; // 1;
          data_rgba[offset + 2] = scanline_buffer[i + off];
          off += scanline_width; // 1;
          data_rgba[offset + 3] = scanline_buffer[i + off];
          offset += 4;
        }

        num_scanlines--;
      }

      return data_rgba;
    };

    const byteArray = new Uint8Array(buffer);
    const byteLength = byteArray.byteLength;
    byteArray['pos'] = 0;
    const rgbe_header_info = this.RGBE_ReadHeader(byteArray);

    if (this.RGBE_RETURN_FAILURE !== rgbe_header_info ) {
      const w = rgbe_header_info.width;
      const h = rgbe_header_info.height;
      const image_rgba_data = this.RGBE_ReadPixels_RLE( byteArray.subarray( byteArray['pos'] ), w, h );
      if (this.RGBE_RETURN_FAILURE !== image_rgba_data ) {
        return {
          width: w, height: h,
          data: image_rgba_data,
          header: rgbe_header_info.string,
          gamma: rgbe_header_info.gamma,
          exposure: rgbe_header_info.exposure,
          format: THREE.RGBEFormat, // handled as THREE.RGBAFormat in shaders
          type: THREE.UnsignedByteType
        };
      }
    }

    return null;
  };
}



// WEBPACK FOOTER //
// ./src/app/hdr-loader.ts